// web/src/admin/AdminSignals.tsx (/admin/signals) — 51_LEARNINGS_LOOP.md §5.
//
// The read side of the learnings loop: what 250 updates/day are teaching, clustered the same way
// the monthly distiller clusters them (same rows, same threshold — the API shapes both through
// one function, so this screen and the PR can never disagree about the numbers).
//
// TWO THINGS THIS SCREEN DELIBERATELY IS NOT:
//   · A trigger. There is no "distill now" button and no endpoint for one — the distiller runs on
//     its own monthly cron behind DISTILL_ENABLED, and the only thing it can do to `brain` is
//     open a PR that KC merges or closes BY HAND (the human-merge rule, doc 51 §2).
//   · A window into customer content. A signal carries request ids, check names and numbers —
//     no title, no body, no chat (§3) — so the examples column is ids, linked to the admin task
//     view where an authorised human can look one up.
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { AsyncBoundary } from '../components/AsyncBoundary';
import { Skeleton } from '../components/Skeleton';
import { KPICard } from '../components/KPICard';
import { Icon } from '../components/Icon';
import { WindowToggle, usd, useAdminWindow } from './adminShared';

const OUTCOME_ORDER = ['live', 'failed', 'needs_attention', 'rejected', 'discarded'] as const;
const OUTCOME_LABEL: Record<string, string> = {
  live: 'Live',
  failed: 'Failed',
  needs_attention: 'Needs attention',
  rejected: 'Rejected',
  discarded: 'Discarded',
  in_flight: 'In flight',
};

export function AdminSignals() {
  const [days, setDays] = useAdminWindow();
  const q = useQuery({
    queryKey: ['admin', 'signals', days],
    queryFn: () => api.adminSignals(days),
  });

  return (
    <>
      <PageHeader
        title="Signals"
        subtitle="What updates are teaching — recurring gate failures, clustered the way the monthly distiller sees them."
      />
      <div className="mb-4">
        <WindowToggle days={days} onChange={setDays} />
      </div>

      <AsyncBoundary query={q} skeleton={<Skeleton variant="card" count={3} />}>
        {(s) => {
          const patterns = s.clusters.filter((c) => c.meetsThreshold);
          const emerging = s.clusters.filter((c) => !c.meetsThreshold);
          const reworkCents = s.clusters.reduce((t, c) => t + c.reworkCents, 0);
          return (
            <>
              <section className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <KPICard label="Signals in window" value={s.totals.signals} />
                <KPICard
                  label={`Patterns (≥ ${s.minSites} sites)`}
                  value={patterns.length}
                  delta={patterns.length ? 'distiller would propose' : undefined}
                  accent={patterns.length ? 'warn' : 'info'}
                />
                <KPICard
                  label="Rework in clusters"
                  value={usd(reworkCents)}
                  accent={reworkCents ? 'warn' : 'info'}
                />
                <KPICard
                  label="Distiller"
                  value={s.distillEnabled ? 'On' : 'Off'}
                  delta="monthly, PR-only"
                  accent={s.distillEnabled ? 'info' : 'warn'}
                />
              </section>

              {/* ── The clusters ──────────────────────────────────────────────────────────── */}
              <section className="mb-6">
                <h2 className="mb-1 font-head text-sm text-ink">Recurring failures</h2>
                <p className="mb-3 text-xs text-muted">
                  A pattern must recur across <b>{s.minSites}+ distinct sites</b> — one noisy
                  customer is not a platform lesson. Examples are request ids; signals carry no
                  customer content.
                </p>
                {s.clusters.length === 0 ? (
                  <p className="flex items-center justify-center gap-2 rounded-lg border border-hair bg-surface/40 px-4 py-8 text-sm text-muted">
                    <Icon name="check" size={16} className="text-success" />
                    No recurring gate failures in this window.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-hair">
                    <table className="w-full min-w-[640px] text-sm">
                      <thead>
                        <tr className="border-b border-hair bg-surface/60 text-left text-xs text-muted">
                          <th className="px-3 py-2 font-normal">Failure</th>
                          <th className="px-3 py-2 font-normal">Kind</th>
                          <th className="px-3 py-2 text-right font-normal">Sites</th>
                          <th className="px-3 py-2 text-right font-normal">Occurrences</th>
                          <th className="px-3 py-2 text-right font-normal">Rework</th>
                          <th className="px-3 py-2 font-normal">Examples</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...patterns, ...emerging].map((c, i) => (
                          <tr
                            key={`${c.failure}-${c.requestKind ?? ''}-${c.siteStack ?? ''}-${i}`}
                            className={`border-b border-hair last:border-0 ${c.meetsThreshold ? 'bg-warn/5' : ''}`}
                          >
                            <td className="px-3 py-2">
                              <span className="rounded-pill border border-danger px-1.5 py-0.5 text-[11px] text-danger">
                                {c.failure}
                              </span>
                              {!c.meetsThreshold && (
                                <span className="ml-1.5 text-[11px] text-muted">emerging</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-xs text-ink">{c.requestKind ?? '—'}</td>
                            <td className="px-3 py-2 text-right font-head text-ink">{c.sites}</td>
                            <td className="px-3 py-2 text-right text-muted">{c.occurrences}</td>
                            <td className="px-3 py-2 text-right font-head text-warn">{usd(c.reworkCents)}</td>
                            <td className="px-3 py-2">
                              <span className="flex flex-wrap gap-1">
                                {c.examples.map((id) => (
                                  <Link
                                    key={id}
                                    to={`/admin/tasks`}
                                    title={id}
                                    className="rounded-pill border border-hair px-1.5 py-0.5 text-[11px] text-muted hover:text-ink"
                                  >
                                    {id.slice(0, 8)}…
                                  </Link>
                                ))}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section className="grid gap-4 lg:grid-cols-2">
                {/* ── Outcomes ────────────────────────────────────────────────────────────── */}
                <div>
                  <h2 className="mb-3 font-head text-sm text-muted">Outcomes in window</h2>
                  <ul className="flex flex-col gap-1.5">
                    {OUTCOME_ORDER.filter((o) => s.totals.byOutcome[o] != null)
                      .concat(
                        Object.keys(s.totals.byOutcome).filter(
                          (o) => !(OUTCOME_ORDER as readonly string[]).includes(o),
                        ) as (typeof OUTCOME_ORDER)[number][],
                      )
                      .map((o) => (
                        <li
                          key={o}
                          className="flex items-center justify-between rounded-md border border-hair bg-surface px-3 py-2 text-sm"
                        >
                          <span className="text-ink">{OUTCOME_LABEL[o] ?? o}</span>
                          <b className="font-head text-ink">{s.totals.byOutcome[o]}</b>
                        </li>
                      ))}
                    {s.totals.signals === 0 && (
                      <li className="rounded-md border border-hair bg-surface/40 px-3 py-6 text-center text-sm text-muted">
                        No signals yet — they appear as updates reach a terminal state.
                      </li>
                    )}
                  </ul>
                </div>

                {/* ── The last distillation PR (§5) ───────────────────────────────────────── */}
                <div>
                  <h2 className="mb-1 font-head text-sm text-muted">Distillation</h2>
                  <p className="mb-3 text-[11px] text-muted">
                    Once a month the distiller clusters these signals and opens <b>one PR</b>{' '}
                    against <code>brain</code> proposing at most three playbook deltas. Nothing is
                    ever merged automatically — the brain grows only by human merge.
                  </p>
                  {s.lastPrUrl ? (
                    <a
                      href={s.lastPrUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair bg-surface px-3 text-sm text-ink hover:border-run"
                    >
                      <Icon name="git" size={16} />
                      Last distillation PR
                      {s.lastPrAt != null && (
                        <span className="text-xs text-muted">
                          {new Date(s.lastPrAt * 1000).toLocaleDateString()}
                        </span>
                      )}
                      <Icon name="external" size={14} className="opacity-60" />
                    </a>
                  ) : (
                    <p className="rounded-lg border border-hair bg-surface/40 px-4 py-6 text-center text-sm text-muted">
                      No distillation PR yet
                      {s.distillEnabled ? ' — the cron runs on the 1st.' : ' — the distiller is off (DISTILL_ENABLED).'}
                    </p>
                  )}
                </div>
              </section>
            </>
          );
        }}
      </AsyncBoundary>
    </>
  );
}
